import React, { useCallback, useState, useMemo, useEffect } from "react";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";

import { Switch } from "../Components";
import { isVideo } from "../utils";
import logger from "../../../../utils/LoggerFrontend";

import {
  Wrapper,
  PostPicturePlaceholder,
  CropperOverflow,
  CropperArea,
  AspectSwitchWrapper,
  ScaleAlert,
  reactEasyCropStyles
} from "./InlineImageCropper.styled";
import { TemplatePostStore, withRefluxStore } from "../../../../Stores";

const MINIMUM_ACCEPTABLE_HEIGHT_FOR_OVERLAYING_CROPPER_IN_PX = 80;
const CROP_PADDING = 200;

const TEMPLATES_WITH_DIMENSIONS = ["WaveImage", "ShapeImage", "OneToneImage"];

const updateDimensionsForTemplates = ({
  templateId,
  orientation = "portrait",
  postPictureWidth,
  maxAspect
}) => {
  let height = parseInt(postPictureWidth / maxAspect);
  let width = postPictureWidth;

  // these magic numbers are matching the % width in remotion templates
  // talentbait-remotion-templates background image
  if (templateId === "ShapeImage") height = height * 0.6;

  if (templateId === "WaveImage") {
    if (orientation === "landscape") width = width * 0.68;
    else if (orientation === "portrait") width = width * 0.63;
    else if (orientation === "vertical") height = height * 0.7;
  }

  if (templateId === "OneToneImage") {
    if (orientation === "vertical") {
      height = height * 0.565; // 1130px height
      width = width * 0.7; // 70% width
    } else {
      height = height * 0.88; // 900px height
      width = width * 0.45; // 45% width
    }
  }

  return { width: Math.round(width), height: Math.round(height) };
};

const InlineImageCropper = ({
  creativeWithoutCrop,
  cropFromCreative,
  setCroppedCreative,
  updateSelectedCreativeOrientation,
  minAspect,
  maxAspect,
  channelMinHeight,
  channelMinWidth,
  extraAspectRatios = [],
  width: postPictureWidth,
  isLogo,
  // Store
  templateId,
  currentInputProps
}) => {
  const { t } = useTranslation();
  const { orientation = "portrait" } = currentInputProps;

  const switchLeftOption = {
    label: t("Landscape")
  };
  const switchRightOption = {
    label: t("Portrait")
  };

  const dimensions = updateDimensionsForTemplates({
    templateId,
    orientation,
    postPictureWidth,
    maxAspect
  });

  const cropperShouldntOverlayImage =
    dimensions.height < MINIMUM_ACCEPTABLE_HEIGHT_FOR_OVERLAYING_CROPPER_IN_PX;

  const creativeIsImage = useMemo(() => !isVideo(creativeWithoutCrop), [creativeWithoutCrop]);

  const [showScaleAlert, setShowScaleAlert] = useState(false);
  const [imageAspect, setImageAspect] = useState();

  // Aspect ratio to use for the cropper
  const [aspect, setAspect] = useState(
    cropFromCreative &&
      cropFromCreative.ar &&
      cropFromCreative.ar <= maxAspect &&
      cropFromCreative.ar >= minAspect
      ? cropFromCreative.ar
      : maxAspect || minAspect
  );

  useEffect(() => {
    if (TEMPLATES_WITH_DIMENSIONS.includes(templateId)) {
      setAspect(dimensions.width / dimensions.height);
    }
  }, [templateId, dimensions]);

  // Switch aspect ratio between min and max
  const switchPreviewMode = useCallback(() => {
    setAspect((currentAspect) => (currentAspect === minAspect ? maxAspect : minAspect));
  }, [maxAspect, minAspect]);

  // Crop properties
  const [crop, setCrop] = useState({
    x: cropFromCreative ? cropFromCreative.x || 0 : 0,
    y: cropFromCreative ? cropFromCreative.y || 0 : 0
  });

  const landscape = useMemo(() => aspect === maxAspect, [aspect, maxAspect]);

  useEffect(() => {
    if (updateSelectedCreativeOrientation) {
      updateSelectedCreativeOrientation(landscape ? "landscape" : "portrait");
    }
  }, [landscape]);

  const onMediaLoaded = useCallback(
    ({ naturalWidth, naturalHeight }) => {
      logger.log("InlineImageCropper:onMediaLoaded", "Media loaded", {
        naturalWidth,
        naturalHeight,
        ar: naturalWidth / naturalHeight,
        extraAspectRatios,
        maxAspect,
        minAspect,
        shouldUpdateAr: extraAspectRatios.includes(
          parseFloat((naturalWidth / naturalHeight).toFixed(2))
        )
      });

      if (
        (channelMinHeight && naturalHeight < channelMinHeight) ||
        (channelMinWidth && naturalWidth < channelMinWidth)
      ) {
        setShowScaleAlert(true);
      } else {
        setShowScaleAlert(false);
      }
      const ar = parseFloat((naturalWidth / naturalHeight).toFixed(2));
      setImageAspect(parseFloat(ar.toFixed(2)));
      if (extraAspectRatios.includes(ar)) {
        logger.log("InlineImageCropper:onMediaLoaded", "setting aspect ratio", { ar });
        setAspect(ar);
      }
    },
    [channelMinHeight, channelMinWidth, extraAspectRatios, maxAspect, minAspect]
  );

  // Create and update cropped creative url on finishing a crop movement
  const onCropCompleted = useCallback(
    (percentCrop, pixelCrop) => {
      let newCreative = creativeWithoutCrop;
      let uploadUrl = `/upload/c_crop,ar_${aspect}/`;

      if (
        (percentCrop.height && percentCrop.height !== 100) ||
        (percentCrop.width && percentCrop.width !== 100) ||
        pixelCrop.x > 0 ||
        pixelCrop.y > 0
      ) {
        uploadUrl = `/upload/c_crop,h_${pixelCrop.height},w_${pixelCrop.width},x_${pixelCrop.x},y_${pixelCrop.y},ar_${aspect}/`;
      } else if (dimensions.height < channelMinHeight) {
        uploadUrl = `/upload/c_mfit,h_${channelMinHeight + 1}/`;
      } else if (dimensions.width < channelMinWidth) {
        uploadUrl = `/upload/c_mfit,w_${channelMinWidth + 1}/`;
      } else {
        uploadUrl = isVideo(newCreative) ? "/upload/" : `/upload/c_crop,ar_${aspect}/`;
      }

      newCreative = newCreative.replace("/upload/", `${uploadUrl}`);
      setCroppedCreative(newCreative);
    },
    [creativeWithoutCrop, setCroppedCreative, aspect, channelMinWidth, channelMinHeight, dimensions]
  );

  const objectFit = useMemo(() => {
    if (!imageAspect || imageAspect === aspect) {
      return "contain";
    }
    if (imageAspect > aspect) {
      return "vertical-cover";
    }
    return "horizontal-cover";
  }, [aspect, imageAspect]);

  const imageCropperSizes = useMemo(() => {
    if (!imageAspect || imageAspect === aspect) return dimensions;

    const horizontalMove = imageAspect > aspect;

    return {
      height: !horizontalMove ? dimensions.height + CROP_PADDING : dimensions.height,
      width: horizontalMove ? dimensions.width + CROP_PADDING : dimensions.width
    };
  }, [aspect, imageAspect, dimensions]);

  return (
    <Wrapper
      height={dimensions.height}
      width={dimensions.width}
      templateId={templateId}
      orientation={orientation}
      shouldntOverlay={cropperShouldntOverlayImage}
      isLogo={isLogo}
    >
      <PostPicturePlaceholder isLogo={isLogo}>
        {showScaleAlert && (
          <ScaleAlert>Vorsicht: Die Auflösung des Bildes ist sehr niedrig.</ScaleAlert>
        )}
        <CropperOverflow height={imageCropperSizes.height} width={imageCropperSizes.width}>
          <CropperArea height={dimensions.height} width={dimensions.width}>
            <Cropper
              key={objectFit}
              {...(creativeIsImage
                ? { image: creativeWithoutCrop }
                : { video: creativeWithoutCrop })}
              crop={crop}
              aspect={aspect}
              zoom={1}
              minZoom={1}
              maxZoom={1}
              zoomWithScroll={false}
              onCropChange={setCrop}
              onCropComplete={onCropCompleted}
              onMediaLoaded={onMediaLoaded}
              objectFit={objectFit}
              initialCroppedAreaPixels={cropFromCreative}
              style={reactEasyCropStyles(dimensions)}
            />
          </CropperArea>
        </CropperOverflow>
      </PostPicturePlaceholder>
      {minAspect !== maxAspect && !extraAspectRatios.includes(aspect) && (
        <AspectSwitchWrapper>
          <Switch
            isLeft={landscape}
            switchAction={switchPreviewMode}
            leftOption={switchLeftOption}
            rightOption={switchRightOption}
            width={1}
          />
        </AspectSwitchWrapper>
      )}
    </Wrapper>
  );
};

export default withRefluxStore(
  InlineImageCropper,
  [TemplatePostStore],
  ["templateId", "currentInputProps"]
);
